import { CarrierIssueType } from "@freightsimple/generated-carriers-openapi-client/models/CarrierIssueType";
import { CustomerSpecificPricingStatus } from "@freightsimple/generated-carriers-openapi-client/models/CustomerSpecificPricingStatus";
import { type PortalCurrency } from "@freightsimple/generated-carriers-openapi-client/models/PortalCurrency";

// Display helpers shared across the carrier portal's data views. The carrier app is intentionally
// thin, so formatting/label maps live here rather than pulling in heavier shared packages.

/**
 * A money amount, with the currency named.
 *
 * The code is not decoration. Half our carriers are US-based and invoice us in USD, so a bare "$"
 * on a carrier-facing figure is genuinely ambiguous — and the stored figures are Canadian dollars,
 * which is a number a US carrier can't reconcile against anything in their own system.
 */
export function formatMoney(amount: number, currency?: PortalCurrency): string {
  // A missing or non-numeric amount renders as an em-dash, not "$NaN". The API types these fields
  // as required, so this only fires when the response doesn't match its own contract — which is
  // exactly when we least want to print nonsense on a carrier's screen.
  if (!Number.isFinite(amount)) return "—";
  const figure = `$${Math.round(amount).toLocaleString("en-CA")}`;
  return currency === undefined ? figure : `${figure} ${currency}`;
}

const ISSUE_TYPE_LABELS: Record<CarrierIssueType, string> = {
  [CarrierIssueType.BookPickup]: "Book Pickup",
  [CarrierIssueType.MissedPickup]: "Missed Pickup",
  [CarrierIssueType.ExpectedDeliveryIsInThePast]: "Delivery Overdue",
  [CarrierIssueType.TrackingNotUpdated]: "Tracking Not Updated",
  [CarrierIssueType.MissedDelivery]: "Missed Delivery",
  [CarrierIssueType.MissingFreight]: "Missing Freight",
  [CarrierIssueType.PendingCustomsClearance]: "Pending Customs Clearance",
};

export function describeIssueType(type: CarrierIssueType): string {
  return ISSUE_TYPE_LABELS[type];
}

const SHIPMENT_STATE_LABELS: Record<string, string> = {
  quoted: "Quoted",
  booking_confirmed: "Booking Confirmed",
  in_transit: "In Transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
  on_hold: "On Hold",
};

export function describeShipmentState(state: string): string {
  return SHIPMENT_STATE_LABELS[state] ?? state;
}

// Panel left-border accent per issue type — mirrors hubble's grouped
// operational-tasks panels (red = service failure, gold = needs follow-up,
// blue = change request). Limited to the colors ui-core's Panel accepts.
type IssueAccent = "red" | "gold" | "blue";

const ISSUE_TYPE_ACCENTS: Record<CarrierIssueType, IssueAccent> = {
  [CarrierIssueType.MissedPickup]: "red",
  [CarrierIssueType.MissedDelivery]: "red",
  [CarrierIssueType.MissingFreight]: "red",
  [CarrierIssueType.ExpectedDeliveryIsInThePast]: "gold",
  [CarrierIssueType.TrackingNotUpdated]: "gold",
  [CarrierIssueType.PendingCustomsClearance]: "gold",
  [CarrierIssueType.BookPickup]: "blue",
};

export function issueAccent(type: CarrierIssueType): IssueAccent {
  return ISSUE_TYPE_ACCENTS[type];
}

// The same severity, as a Badge variant — derived from the panel accent so a
// shipment row and the Operational Issues panel can't grade the same issue
// differently.
const ACCENT_BADGE_VARIANTS = {
  red: "danger",
  gold: "warning",
  blue: "info",
} as const;

export function issueBadgeVariant(
  type: CarrierIssueType,
): (typeof ACCENT_BADGE_VARIANTS)[IssueAccent] {
  return ACCENT_BADGE_VARIANTS[issueAccent(type)];
}

// Shipment state → StatusDot color, matching hubble's StatusDot conventions
// (gold booking-confirmed, blue in-transit, green delivered).
type ShipmentDotColor = "gold" | "blue" | "green" | "red" | "orange" | "grey";

const SHIPMENT_STATE_DOT_COLORS: Record<string, ShipmentDotColor> = {
  quoted: "grey",
  booking_confirmed: "gold",
  in_transit: "blue",
  delivered: "green",
  cancelled: "red",
  on_hold: "orange",
};

export function shipmentStateDotColor(state: string): ShipmentDotColor {
  return SHIPMENT_STATE_DOT_COLORS[state] ?? "grey";
}

// Customer-specific-pricing status → StatusDot color. Green for active, gold to
// signal the carrier's pricing is awaited, blue while being set up, and muted
// reds/greys/purple for the ended states.
type CspDotColor = "green" | "gold" | "blue" | "red" | "grey" | "purple";

const CSP_STATUS_DOT_COLORS: Record<
  CustomerSpecificPricingStatus,
  CspDotColor
> = {
  [CustomerSpecificPricingStatus.Active]: "green",
  [CustomerSpecificPricingStatus.AwaitingPricing]: "gold",
  [CustomerSpecificPricingStatus.BeingSetUp]: "blue",
  [CustomerSpecificPricingStatus.Declined]: "red",
  [CustomerSpecificPricingStatus.Cancelled]: "grey",
  [CustomerSpecificPricingStatus.ExistingCustomer]: "purple",
};

export function cspStatusDotColor(
  status: CustomerSpecificPricingStatus,
): CspDotColor {
  return CSP_STATUS_DOT_COLORS[status] ?? "grey";
}

export function formatPercent(rate: number): string {
  // Same guard as formatMoney: an absent rate is an em-dash, never "NaN%".
  if (!Number.isFinite(rate)) return "—";
  return `${Math.round(rate * 100)}%`;
}
